/*
 * Developed for G.J. Gardner Homes by Softeq Development Corporation
 * http://www.softeq.com
 */

import {
  AppFormGroup,
  ControlSchemaPackedValue,
  ControlSchemaType,
  FormRootSchema,
  FormRootSchemaConfig,
  FormState,
} from '@gh/common-shared';
import { FormMode } from '@gh/core-ui';
import { isNotNil } from '@gh/core-util';
import { create, fromPairs, isNil, mapValues } from 'lodash';
import { whenOfficeUser } from '../app-state/builder-application-state-util';
import { ensureControlSchema } from './builder-form-control-schema';
import { BuilderFormGroupSchema, ensureGroupSchema } from './builder-form-group-schema';
import { BuilderFormStateSchema } from './builder-form-state-schema';

export class BuilderFormRootSchema extends BuilderFormGroupSchema implements FormRootSchema {
  schemaType = ControlSchemaType.Root;

  mode?: FormMode;

  constructor(private stateSchema: BuilderFormStateSchema) {
    super(stateSchema);
  }

  update(config: FormRootSchemaConfig): void {
    const children = { ...(config.children || this.children) };
    if (config.optimisticLock) {
      children.etag = this.children['optimisticLock'] || ensureControlSchema(this.stateHolder, {});
    }
    if (config.privilegedEntryPoints) {
      children.privilegedTokens = this.children['privilegedEntryPoints'] || ensureGroupSchema(this.stateHolder, {
        children: fromPairs(config.privilegedEntryPoints
          .map((entryPoint) => [entryPoint, { visible: whenOfficeUser() }])),
      });
    }
    if (config.confirmations) {
      children.confirmations = this.children['confirmations'] || ensureGroupSchema(this.stateHolder, {
        children: mapValues(config.confirmations, () => void 0),
      });
    }

    super.update(create(config, { children }));

    this.mode = isNil(config.mode) ? this.mode : config.mode;
  }

  applyTo(control: AppFormGroup, packedValue?: ControlSchemaPackedValue): void {
    const { mode, state: { currentForm } } = this;
    if (isNotNil(mode) && currentForm) {
      currentForm.setMode(mode);
    }
    super.applyTo(control, packedValue);
  }

  init(formState: FormState<any>): void {
    const { mode } = this;

    if (!isNil(mode)) {
      formState.setMode(mode);
    }

    this.stateSchema.init(formState);
  }

  use(schema: FormRootSchema): void {
    this.stateSchema.useState(schema.state);
  }
}
